<template>
  <b-modal
    ref="modal"
    :title="isEditing ? 'Edit A/B Test' : 'Create A/B Test'"
    size="lg"
    centered
    scrollable
    @hidden="onHidden"
    @ok="saveTest"
  >
    <div class="create-ab-test-modal">
      <!-- Step 1: Test Type Selection -->
      <div class="form-section" v-if="currentStep === 1">
        <h6 class="section-title">What do you want to test?</h6>
        <div class="test-type-options">
          <label
            class="type-option"
            :class="{ selected: form.Type === 'SubjectLine' }"
          >
            <input type="radio" v-model="form.Type" value="SubjectLine" class="d-none" />
            <div class="option-icon">
              <i class="fa fa-font"></i>
            </div>
            <div class="option-content">
              <h5>Subject Line</h5>
              <p>Test different subject lines with the same email content</p>
            </div>
          </label>

          <label
            class="type-option"
            :class="{ selected: form.Type === 'Content' }"
          >
            <input type="radio" v-model="form.Type" value="Content" class="d-none" />
            <div class="option-icon">
              <i class="fa fa-file-text"></i>
            </div>
            <div class="option-content">
              <h5>Content</h5>
              <p>Test different email content with the same subject line</p>
            </div>
          </label>
        </div>
      </div>

      <!-- Step 2: Test Details -->
      <div class="form-section" v-if="currentStep === 2">
        <h6 class="section-title">Test Details</h6>

        <b-form-group label="Test Name" label-for="test-name">
          <b-form-input
            id="test-name"
            v-model="form.Name"
            placeholder="e.g., Recall Email Subject Test"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Linked List" label-for="test-list">
          <b-form-select
            id="test-list"
            v-model="form.LinkedList"
            :options="listOptions"
          ></b-form-select>
        </b-form-group>

        <div class="row">
          <div class="col-md-6">
            <b-form-group label="From Name" label-for="from-name">
              <b-form-input
                id="from-name"
                v-model="form.FromName"
                placeholder="Your Practice Name"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Reply-to Email" label-for="reply-to">
              <b-form-input
                id="reply-to"
                v-model="form.ReplyTo"
                type="email"
                placeholder="reply@example.com"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Step 3: Variants -->
      <div class="form-section" v-if="currentStep === 3">
        <h6 class="section-title">
          {{ form.Type === 'SubjectLine' ? 'Subject Line Variants' : 'Content Variants' }}
        </h6>

        <div class="variants-container">
          <div
            v-for="(variant, index) in form.Variants"
            :key="index"
            class="variant-editor"
            :class="'variant-' + String.fromCharCode(65 + index).toLowerCase()"
          >
            <div class="variant-header">
              <span class="variant-label">Variant {{ String.fromCharCode(65 + index) }}</span>
              <button
                v-if="form.Variants.length > 2"
                class="remove-variant-btn"
                @click="removeVariant(index)"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>

            <!-- Subject Line Input -->
            <b-form-group :label="form.Type === 'SubjectLine' ? 'Subject Line' : 'Subject Line (same for all)'" class="mb-2">
              <div class="subject-input-wrapper">
                <b-form-input
                  v-model="variant.subject"
                  placeholder="Enter subject line..."
                  :readonly="form.Type === 'Content' && index > 0"
                ></b-form-input>
                <div class="subject-tools">
                  <b-dropdown variant="link" no-caret size="sm">
                    <template #button-content>
                      <i class="fa fa-smile-o"></i>
                    </template>
                    <div class="emoji-grid">
                      <span
                        v-for="emoji in commonEmojis"
                        :key="emoji"
                        class="emoji-option"
                        @click="insertEmoji(variant, 'subject', emoji)"
                      >{{ emoji }}</span>
                    </div>
                  </b-dropdown>
                  <b-dropdown variant="link" no-caret size="sm">
                    <template #button-content>
                      <i class="fa fa-code"></i>
                    </template>
                    <b-dropdown-item @click="insertVariable(variant, 'subject', '{{FirstName}}')">
                      {{FirstName}}
                    </b-dropdown-item>
                    <b-dropdown-item @click="insertVariable(variant, 'subject', '{{LastName}}')">
                      {{LastName}}
                    </b-dropdown-item>
                    <b-dropdown-item @click="insertVariable(variant, 'subject', '{{PracticeName}}')">
                      {{PracticeName}}
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </div>
              <small class="char-counter" :class="{ warning: variant.subject?.length > 60 }">
                {{ (variant.subject || '').length }} / 60 characters (recommended)
              </small>
            </b-form-group>

            <!-- Content Editor (for Content tests) -->
            <b-form-group v-if="form.Type === 'Content'" label="Email Content">
              <b-form-textarea
                v-model="variant.content"
                placeholder="Enter email content..."
                rows="6"
              ></b-form-textarea>
            </b-form-group>
          </div>

          <button
            v-if="form.Variants.length < 5"
            class="add-variant-btn"
            @click="addVariant"
          >
            <i class="fa fa-plus mr-2"></i>
            Add Variant {{ String.fromCharCode(65 + form.Variants.length) }}
          </button>
        </div>
      </div>

      <!-- Step 4: Test Settings -->
      <div class="form-section" v-if="currentStep === 4">
        <h6 class="section-title">Test Settings</h6>

        <!-- Test Group Size -->
        <div class="setting-group">
          <label>Test Group Size</label>
          <div class="slider-container">
            <div class="slider-labels">
              <span>Test variants: {{ form.TestGroupSize }}%</span>
              <span>Winner: {{ 100 - form.TestGroupSize }}%</span>
            </div>
            <b-form-input
              type="range"
              v-model.number="form.TestGroupSize"
              min="10"
              max="50"
              step="5"
            ></b-form-input>
            <div class="slider-visual">
              <div
                class="test-portion"
                :style="{ width: form.TestGroupSize + '%' }"
              >
                <span v-for="(v, i) in form.Variants" :key="i" class="variant-indicator">
                  {{ String.fromCharCode(65 + i) }}
                </span>
              </div>
              <div
                class="winner-portion"
                :style="{ width: (100 - form.TestGroupSize) + '%' }"
              >
                <i class="fa fa-trophy"></i> Winner
              </div>
            </div>
          </div>
        </div>

        <!-- Winner Criteria -->
        <b-form-group label="Determine winner by">
          <b-form-radio-group v-model="form.WinnerCriteria" stacked>
            <b-form-radio value="OpenRate">
              <strong>Open Rate</strong>
              <small class="d-block text-muted">Best for testing subject lines</small>
            </b-form-radio>
            <b-form-radio value="ClickRate">
              <strong>Click Rate</strong>
              <small class="d-block text-muted">Best for testing content</small>
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <!-- Test Duration -->
        <b-form-group label="Test Duration">
          <b-form-select v-model="form.TestDuration" :options="durationOptions"></b-form-select>
          <small class="text-muted">Time to wait before determining the winner</small>
        </b-form-group>

        <!-- Automatic Winner -->
        <b-form-group>
          <b-form-checkbox v-model="form.AutoSendWinner">
            <strong>Automatically send winner</strong>
            <small class="d-block text-muted">
              Automatically send the winning variant to remaining recipients after the test duration
            </small>
          </b-form-checkbox>
        </b-form-group>

        <!-- Tracking Options -->
        <div class="tracking-options">
          <h6>Tracking</h6>
          <b-form-checkbox v-model="form.TrackClicks">Track clicks</b-form-checkbox>
          <b-form-checkbox v-model="form.TrackGoogleAnalytics">Google Analytics tracking</b-form-checkbox>
        </div>
      </div>

      <!-- Step Navigation -->
      <div class="step-indicators">
        <div
          v-for="step in totalSteps"
          :key="step"
          class="step-dot"
          :class="{ active: currentStep === step, completed: currentStep > step }"
          @click="goToStep(step)"
        ></div>
      </div>
    </div>

    <template #modal-footer>
      <b-button
        v-if="currentStep > 1"
        variant="outline-secondary"
        @click="previousStep"
      >
        <i class="fa fa-arrow-left mr-2"></i> Back
      </b-button>
      <b-button v-else variant="outline-secondary" @click="$refs.modal.hide()">
        Cancel
      </b-button>

      <b-button
        v-if="currentStep < totalSteps"
        variant="primary"
        @click="nextStep"
        :disabled="!canProceed"
      >
        Next <i class="fa fa-arrow-right ml-2"></i>
      </b-button>
      <b-button
        v-else
        variant="success"
        @click="saveTest"
        :disabled="!canSave"
      >
        <i class="fa fa-check mr-2"></i>
        {{ isEditing ? 'Update Test' : 'Create Test' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'CreateABTestModal',

  props: {
    test: {
      type: Object,
      default: null
    },
    isEditing: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentStep: 1,
      totalSteps: 4,

      form: {
        Name: '',
        Type: 'SubjectLine',
        LinkedList: null,
        FromName: '',
        ReplyTo: '',
        Variants: [
          { subject: '', content: '' },
          { subject: '', content: '' }
        ],
        TestGroupSize: 30,
        WinnerCriteria: 'OpenRate',
        TestDuration: '24h',
        AutoSendWinner: true,
        TrackClicks: true,
        TrackGoogleAnalytics: false
      },

      listOptions: [
        { value: null, text: 'Select a list...' },
        { value: 'list1', text: 'Active Patients' },
        { value: 'list2', text: 'Recall Due' },
        { value: 'list3', text: 'New Patients' }
      ],

      durationOptions: [
        { value: '4h', text: '4 hours' },
        { value: '12h', text: '12 hours' },
        { value: '24h', text: '24 hours' },
        { value: '48h', text: '48 hours' },
        { value: '72h', text: '72 hours' }
      ],

      commonEmojis: ['😀', '😊', '🎉', '✨', '🔥', '💪', '👋', '❤️', '⭐', '🌟', '💯', '🎁']
    };
  },

  computed: {
    canProceed() {
      switch (this.currentStep) {
        case 1:
          return !!this.form.Type;
        case 2:
          return !!this.form.Name;
        case 3:
          return this.form.Variants.every(v => v.subject?.trim());
        default:
          return true;
      }
    },

    canSave() {
      return this.form.Name &&
        this.form.Variants.length >= 2 &&
        this.form.Variants.every(v => v.subject?.trim());
    }
  },

  watch: {
    test: {
      immediate: true,
      handler(newTest) {
        if (newTest) {
          this.form = {
            ...this.form,
            Name: newTest.Name,
            Type: newTest.Type,
            WinnerCriteria: newTest.WinnerCriteria,
            TestDuration: newTest.TestDuration,
            Variants: [
              { subject: newTest.VariantA?.subject || '', content: '' },
              { subject: newTest.VariantB?.subject || '', content: '' }
            ]
          };
        }
      }
    }
  },

  methods: {
    show() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.$refs.modal.show();
    },

    resetForm() {
      this.currentStep = 1;
      this.form = {
        Name: '',
        Type: 'SubjectLine',
        LinkedList: null,
        FromName: '',
        ReplyTo: '',
        Variants: [
          { subject: '', content: '' },
          { subject: '', content: '' }
        ],
        TestGroupSize: 30,
        WinnerCriteria: 'OpenRate',
        TestDuration: '24h',
        AutoSendWinner: true,
        TrackClicks: true,
        TrackGoogleAnalytics: false
      };
    },

    goToStep(step) {
      if (step <= this.currentStep) {
        this.currentStep = step;
      }
    },

    nextStep() {
      if (this.canProceed && this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    addVariant() {
      if (this.form.Variants.length < 5) {
        this.form.Variants.push({ subject: '', content: '' });
      }
    },

    removeVariant(index) {
      if (this.form.Variants.length > 2) {
        this.form.Variants.splice(index, 1);
      }
    },

    insertEmoji(variant, field, emoji) {
      variant[field] = (variant[field] || '') + emoji;
    },

    insertVariable(variant, field, variable) {
      variant[field] = (variant[field] || '') + variable;
    },

    saveTest(event) {
      if (event) event.preventDefault();

      if (!this.canSave) return;

      const testData = {
        AbTestID: this.isEditing ? this.test.AbTestID : Date.now().toString(),
        Name: this.form.Name,
        Type: this.form.Type,
        Status: 'Draft',
        CreatedAt: this.isEditing ? this.test.CreatedAt : new Date().toISOString(),
        VariantA: {
          subject: this.form.Variants[0].subject,
          content: this.form.Variants[0].content,
          sent: 0,
          opened: 0,
          clicked: 0,
          openRate: 0,
          clickRate: 0
        },
        VariantB: {
          subject: this.form.Variants[1].subject,
          content: this.form.Variants[1].content,
          sent: 0,
          opened: 0,
          clicked: 0,
          openRate: 0,
          clickRate: 0
        },
        Winner: null,
        WinnerCriteria: this.form.WinnerCriteria === 'OpenRate' ? 'Open Rate' : 'Click Rate',
        TestDuration: this.form.TestDuration,
        TestGroupSize: this.form.TestGroupSize,
        AutoSendWinner: this.form.AutoSendWinner
      };

      this.$emit('saved', testData);
      this.$refs.modal.hide();
    },

    onHidden() {
      this.$emit('hidden');
    }
  }
};
</script>

<style lang="scss" scoped>
.create-ab-test-modal {
  .form-section {
    margin-bottom: 24px;

    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #212529;
      margin-bottom: 16px;
      text-transform: uppercase;
    }
  }

  .test-type-options {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    .type-option {
      display: flex;
      gap: 16px;
      padding: 20px;
      border: 2px solid #dee2e6;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: #adb5bd;
      }

      &.selected {
        border-color: #007bff;
        background: #f8f9ff;
      }

      .option-icon {
        width: 50px;
        height: 50px;
        background: #e9ecef;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 24px;
          color: #6c757d;
        }
      }

      &.selected .option-icon {
        background: #007bff;

        i {
          color: #fff;
        }
      }

      .option-content {
        flex: 1;

        h5 {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 4px;
        }

        p {
          font-size: 13px;
          color: #6c757d;
          margin: 0;
        }
      }
    }
  }

  .variants-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .variant-editor {
    border: 2px solid #dee2e6;
    border-radius: 8px;
    padding: 16px;

    &.variant-a {
      border-left-color: #667eea;
      border-left-width: 4px;
    }

    &.variant-b {
      border-left-color: #764ba2;
      border-left-width: 4px;
    }

    &.variant-c {
      border-left-color: #28a745;
      border-left-width: 4px;
    }

    &.variant-d {
      border-left-color: #fd7e14;
      border-left-width: 4px;
    }

    &.variant-e {
      border-left-color: #17a2b8;
      border-left-width: 4px;
    }

    .variant-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .variant-label {
        font-weight: 600;
        font-size: 13px;
        color: #6c757d;
        text-transform: uppercase;
      }

      .remove-variant-btn {
        background: none;
        border: none;
        color: #dc3545;
        cursor: pointer;
        padding: 4px 8px;

        &:hover {
          background: #f8d7da;
          border-radius: 4px;
        }
      }
    }

    .subject-input-wrapper {
      position: relative;

      .subject-tools {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        gap: 4px;
      }
    }

    .char-counter {
      font-size: 11px;
      color: #6c757d;

      &.warning {
        color: #ffc107;
      }
    }

    .emoji-grid {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 4px;
      padding: 8px;

      .emoji-option {
        padding: 4px;
        text-align: center;
        cursor: pointer;
        border-radius: 4px;

        &:hover {
          background: #f8f9fa;
        }
      }
    }
  }

  .add-variant-btn {
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 16px;
    color: #6c757d;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #007bff;
      color: #007bff;
    }
  }

  .setting-group {
    margin-bottom: 24px;

    > label {
      font-weight: 500;
      margin-bottom: 8px;
      display: block;
    }
  }

  .slider-container {
    .slider-labels {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      color: #6c757d;
      margin-bottom: 8px;
    }

    .slider-visual {
      display: flex;
      height: 40px;
      border-radius: 8px;
      overflow: hidden;
      margin-top: 12px;

      .test-portion {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #fff;
        font-weight: 600;
        font-size: 14px;

        .variant-indicator {
          background: rgba(255, 255, 255, 0.3);
          padding: 2px 8px;
          border-radius: 4px;
        }
      }

      .winner-portion {
        background: #ffc107;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #212529;
        font-weight: 600;
        font-size: 14px;
      }
    }
  }

  .tracking-options {
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;

    h6 {
      margin-bottom: 12px;
    }
  }

  .step-indicators {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;

    .step-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #dee2e6;
      cursor: pointer;
      transition: all 0.2s;

      &.active {
        background: #007bff;
        transform: scale(1.2);
      }

      &.completed {
        background: #28a745;
      }
    }
  }
}

@media (max-width: 576px) {
  .create-ab-test-modal .test-type-options {
    grid-template-columns: 1fr;
  }
}
</style>
